import { ChangeEvent, ReactNode, useEffect, useRef } from 'react';
import { useFormWorkerState } from '@feature/dynamic_form/form_manager/use_form_worker';
import type { FormWorker } from '@feature/dynamic_form/form_manager/form_worker';
import { Flex } from '@cellula/react';
import { tokens } from '@cellula/react-theme-patient';
import { Timer } from '@visiba/std';
import { DynamicFormContext } from './dynamic_form.context';
import { Shimmer } from '@component/new_shimmer.component';

function DynamicFormRootSkeleton() {
	return (
		<Flex gap={tokens.spacing.x3}>
			<Flex gap={tokens.spacing.x1}>
				<Shimmer height={tokens.size.x3} width='60%' />
			</Flex>
			<Flex gap={tokens.spacing.x1}>
				<Shimmer height={tokens.size.x2} width='30%' />
				<Shimmer height={tokens.size.x9} />
			</Flex>
			<Flex gap={tokens.spacing.x1}>
				<Shimmer height={tokens.size.x2} width='42%' />
				<Shimmer height={tokens.size.x5} />
			</Flex>
		</Flex>
	);
}

interface Props {
	defaultPagePosition?: number;
	formWorker: Readonly<FormWorker>;
	children: ReactNode;
}

export function DynamicFormRoot({ formWorker, defaultPagePosition = 0, children }: Props): JSX.Element {
	const { meta, pagePosition } = useFormWorkerState(formWorker);
	const containerRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		formWorker.resetPosition(defaultPagePosition);

		return Timer.wait(() => {
			formWorker.getData();
		}, 500); // Waiting a bit to let the sheet open, avoid cluttering the main thread.;
	}, [formWorker, defaultPagePosition]);

	useEffect(
		function whenPageIsChanged() {
			containerRef.current?.focus();
		},
		[pagePosition],
	);

	function handleGoBack(): void {
		formWorker.changePage('backwards', {});
	}

	function handleSubmit(event: ChangeEvent<HTMLFormElement>): void {
		event.preventDefault();

		formWorker.changePage('forwards', event.target);
	}

	return (
		<DynamicFormContext.Provider value={{ formWorker, handleSubmit, handleGoBack }}>
			{meta == null ? (
				<DynamicFormRootSkeleton />
			) : (
				<Flex grow={1} gap={tokens.spacing.x2} ref={containerRef} tabIndex={-1} style={{ outline: 'none' }}>
					{children}
				</Flex>
			)}
		</DynamicFormContext.Provider>
	);
}
